/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import { Groups } from "@mui/icons-material"
import PrintIcon from "@mui/icons-material/Print"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import FullCalendar from "@fullcalendar/react"
import esLocale from "@fullcalendar/core/locales/es"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import CustomBackdrop from "../../components/CustomBackdrop"
import ModalUsuarios from "./components/ModalUsuarios"
import ModalPlanificacionPaquetes from "./components/ModalPlanificacionPaquetes"
import { showSuccess, showError, showConfirmation, showServerError } from "./utils/alertUtils"
import dayjs from "./utils/dayjsConfig"
import { useQuery } from "@tanstack/react-query"
import fetchwrapper from "../../services/interceptors/fetchwrapper"
import { useNavigate } from "react-router-dom"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

// Renderizado optimizado de eventos (Can Grow / Sin recortes de texto)
const renderEventContent = (eventInfo) => {
  const event = eventInfo.event
  const startTime = event.start ? dayjs(event.start).format("HH:mm") : ""
  const endTime = event.end ? dayjs(event.end).format("HH:mm") : ""

  return (
    <div
      style={{
        padding: "2px 4px",
        fontSize: "0.7rem",
        lineHeight: "1.2",
        width: "100%",
        height: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.65rem",
          marginBottom: "1px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {startTime} - {endTime}
      </div>
      <div
        style={{
          wordWrap: "break-word",
          wordBreak: "break-word",
          whiteSpace: "normal",
          overflow: "visible",
          flex: 1,
        }}
      >
        {eventInfo.event.title}
      </div>
    </div>
  )
}

// Tooltip mejorado con la nueva estructura de datos
const handleEventDidMount = (info) => {
  const event = info.event
  const startTime = event.start ? dayjs(event.start).format("HH:mm") : ""
  const endTime = event.end ? dayjs(event.end).format("HH:mm") : ""
  const extendedProps = event.extendedProps || {}

  const tooltip = `
    Título: ${event.title}
    Estado: ${extendedProps.status || "Sin estado"}
    Cliente: ${extendedProps.cliente || extendedProps.clinombre || "Sin cliente"}
    Usuario: ${extendedProps.usuario || extendedProps.usrnombre || "Sin usuario"}
    Horario: ${startTime} - ${endTime}
    Duración: ${extendedProps.duracion || 0} minutos
    Avance: ${extendedProps.avance || 0}%
    ${extendedProps.recurrencia ? `Recurrencia: ${extendedProps.recurrencia}` : ""}
  `.trim()

  info.el.setAttribute("title", tooltip)
}

// Función para obtener eventos desde la API
function useGetEventos(filtros) {
  return useQuery({
    queryKey: ["CrearPlanificacionTareas", "getAllEventsCalendar", filtros],
    queryFn: async () => {
      try {
        const options = {
          method: "POST",
          body: JSON.stringify({
            fecha_inicio: filtros.fechaInicio,
            fecha_fin: filtros.fechaFin,
            estados: filtros.estados,
            usuarios: filtros.usuarios,
          }),
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }

        const response = await fetchwrapper(`/PlanificacionTareas/getAllEventsCalendar`, options)
        const result = await response.json()

        if (!result.success) {
          throw new Error(result.message || "Error al obtener eventos")
        }

        return result.data
      } catch (error) {
        console.error("Error fetching eventos:", error)
        throw error
      }
    },
    enabled: true,
    onError: (error) => {
      console.log("Error fetching eventos:", error.message)
    },
  })
}

// Componente Principal
const CrearPlanificacionTareas = () => {
  const navigate = useNavigate()
  const calendarRef = useRef(null)
  const [eventos, setEventos] = useState([])
  const [showPlanificacionModal, setShowPlanificacionModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [modalUsuariosOpen, setModalUsuariosOpen] = useState(false)
  const [loading, setLoading] = useState({ eventos: true })
  const [isLoadingDeletingEventos, setIsLoadingDeletingEventos] = useState(false)

  // Filtros aplicados (los que se envían al backend)
  const [filtros, setFiltros] = useState({
    fechaInicio: dayjs().startOf("month").format("YYYY-MM-DD"),
    fechaFin: dayjs().endOf("month").format("YYYY-MM-DD"),
    estados: ["PENDIENTE", "EN_PROCESO", "COMPLETADA", "REPROGRAMADA", "CANCELADA"],
    usuarios: [],
  })

  // Filtros temporales (los que el usuario está editando)
  const [filtrosTemp, setFiltrosTemp] = useState({
    fechaInicio: dayjs().startOf("month").format("YYYY-MM-DD"),
    fechaFin: dayjs().endOf("month").format("YYYY-MM-DD"),
    estados: ["PENDIENTE", "EN_PROCESO", "COMPLETADA", "REPROGRAMADA", "CANCELADA"],
    usuarios: [],
  })

  // Query para obtener eventos desde la API
  const {
    data: eventosApi = [],
    isLoading: isLoadingEventos,
    isError: isErrorEventos,
    isFetching: isFetchingEventos,
    refetch: refetchEventos,
  } = useGetEventos(filtros)

  const {
    data: usuariosFake = [],
    isLoading: isLoadingUsuarios,
    isError: isErrorUsuarios,
    isFetching: isFetchingUsuarios,
  } = useGetDataUsuarios()

  function useGetDataUsuarios() {
    return useQuery({
      queryKey: ["CrearPlanificacionTareas", "getAllUsuarios"],
      queryFn: async () => {
        const response = await fetchwrapper(`/PlanificacionTareas/getAllUsuarios`)
        const result = await response.json()
        return result.data
      },
      onError: () => {
        console.log("Error fetching data")
      },
    })
  }

  const {
    data: paquetesFake = { tareas: [], paquetes: [] },
    isLoading: isLoadingPaquetes,
    isError: isErrorPaquetes,
    isFetching: isFetchingPaquetes,
  } = useGetDataUPaquetes()

  function useGetDataUPaquetes() {
    return useQuery({
      queryKey: ["CrearPlanificacionTareas", "getAllPaquetes"],
      queryFn: async () => {
        const response = await fetchwrapper(`/PlanificacionTareas/getAllPaquetesYTareas`)
        const result = await response.json()
        return result.data
      },
      onError: () => {
        console.log("Error fetching data")
      },
    })
  }

  // Refetch cuando se aplican filtros nuevos
  useEffect(() => {
    refetchEventos()

    if (calendarRef.current && filtros.fechaInicio) {
      const calendarApi = calendarRef.current.getApi()
      calendarApi.gotoDate(filtros.fechaInicio)
    }
  }, [filtros])

  // Actualizar eventos cuando lleguen de la API
  useEffect(() => {
    if (eventosApi && Array.isArray(eventosApi)) {
      setEventos(eventosApi)
      setLoading({ eventos: false })
    }
  }, [eventosApi])

  // Manejar error de eventos
  useEffect(() => {
    if (isErrorEventos) {
      showError("Error al cargar los eventos del calendario")
      setLoading({ eventos: false })
    }
  }, [isErrorEventos])

  const handleDateSelect = (selectInfo) => {
    const fecha = selectInfo.start
    setSelectedDate(fecha)
    setShowPlanificacionModal(true)
  }

  const handleEventClick = (info) => {
    setSelectedEvent(info.event)
    setShowDetailsModal(true)
  }

  const handleGuardarPlanificacion = async (nuevosEventos) => {
    try {
      console.log("Guardando eventos:", nuevosEventos)
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setEventos((prev) => [...prev, ...nuevosEventos])
      await showSuccess("Paquete planificado exitosamente")
      refetchEventos()
    } catch (error) {
      console.error("Error al guardar planificación:", error)
      await showServerError(error, "Error al guardar la planificación")
      throw error
    }
  }

  const handleDeleteTarea = async (tareas = []) => {
    if (!tareas || tareas.length === 0) return

    try {
      const result = await showConfirmation("¡No podrás revertir esta acción!")
      if (!result.isConfirmed) return

      setIsLoadingDeletingEventos(true)

      const options = {
        method: "POST",
        body: JSON.stringify({ eventos: tareas }),
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      }

      await fetchwrapper("/PlanificacionTareas/deleteEventosPlanificados", options)

      setShowDetailsModal(false)
      setSelectedEvent(null)

      await showSuccess("Los eventos han sido eliminados.")
      navigate(0)
    } catch (error) {
      console.error("Error al eliminar el evento:", error)
      await showServerError(error, "Error al eliminar el evento")
    } finally {
      setIsLoadingDeletingEventos(false)
    }
  }

  // Manejar cambio de checkboxes de estados (filtros temporales)
  const handleEstadoChange = (estado) => {
    setFiltrosTemp((prev) => {
      const estadosActuales = prev.estados
      const estadoExiste = estadosActuales.includes(estado)

      let nuevosEstados
      if (estadoExiste) {
        nuevosEstados = estadosActuales.filter((e) => e !== estado)
      } else {
        nuevosEstados = [...estadosActuales, estado]
      }

      return {
        ...prev,
        estados: nuevosEstados,
      }
    })
  }

  // Manejar cambio de usuarios (multitag)
  const handleUsuariosChange = (event, newValue) => {
    setFiltrosTemp((prev) => ({
      ...prev,
      usuarios: newValue.map((u) => u.usrcodigo),
    }))
  }

  // Aplicar filtros (pasar de temp a aplicados)
  const handleAplicarFiltros = () => {
    setFiltros(filtrosTemp)
  }

  // Limpiar filtros
  const handleLimpiarFiltros = () => {
    const filtrosDefault = {
      fechaInicio: dayjs().startOf("month").format("YYYY-MM-DD"),
      fechaFin: dayjs().endOf("month").format("YYYY-MM-DD"),
      estados: ["PENDIENTE", "EN_PROCESO", "COMPLETADA", "REPROGRAMADA", "CANCELADA"],
      usuarios: [],
    }

    setFiltrosTemp(filtrosDefault)
    setFiltros(filtrosDefault)
  }

  // Función para imprimir o exportar a PDF exclusivamente el calendario
  const handlePrintCalendar = () => {
    const calendarEl = document.querySelector(".fc")
    if (!calendarEl) {
      return showError("No se encontró el contenedor del calendario para imprimir.")
    }

    const usuariosSeleccionadosNombres = usuariosFake
      .filter((u) => filtros.usuarios.includes(u.usrcodigo))
      .map((u) => u.usrnombre)
      .join(", ")

    const tituloImpresion = usuariosSeleccionadosNombres
      ? `Planificación de Tareas (${dayjs(filtros.fechaInicio).format("DD/MM/YYYY")} al ${dayjs(filtros.fechaFin).format("DD/MM/YYYY")})<br/><span style="font-size: 16px; color: #555;">de ${usuariosSeleccionadosNombres}</span>`
      : `Planificación de Tareas (${dayjs(filtros.fechaInicio).format("DD/MM/YYYY")} al ${dayjs(filtros.fechaFin).format("DD/MM/YYYY")})`

    const printWindow = window.open("", "_blank")
    printWindow.document.write(`
      <html>
        <head>
          <title>Planificación de Tareas - Calendario</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h2 { text-align: center; color: #196C87; margin-bottom: 20px; line-height: 1.4; }
            .fc { max-width: 100%; margin: 0 auto; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            @media print {
              .fc-button-group, .fc-toolbar-chunk button { display: none !important; }
            }
          </style>
          <link href="https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/main.min.css" rel="stylesheet" />
        </head>
        <body>
          <h2>${tituloImpresion}</h2>
          <div>${calendarEl.innerHTML}</div>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            }
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  // Calcular estadísticas
  const estadisticas = {
    totalEventos: eventos.length,
    eventosPendientes: eventos.filter((e) => e.extendedProps?.status === "PENDIENTE").length,
    eventosEnProceso: eventos.filter((e) => e.extendedProps?.status === "EN_PROCESO").length,
    eventosCompletados: eventos.filter((e) => e.extendedProps?.status === "COMPLETADA").length,
    eventosReprogramados: eventos.filter((e) => e.extendedProps?.status === "REPROGRAMADA").length,
    eventosCancelados: eventos.filter((e) => e.extendedProps?.status === "CANCELADA").length,
  }

  const isLoading = isLoadingEventos || isFetchingEventos

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Planificación de Tareas</b>
        </div>

        {isLoading ? (
          <CustomBackdrop
            isLoading={
              isLoadingUsuarios ||
              isFetchingUsuarios ||
              isLoadingPaquetes ||
              isFetchingPaquetes ||
              isLoading ||
              isLoadingDeletingEventos
            }
          />
        ) : (
          <Box className="row">
            {/* Calendar Sidebar */}
            <div className="col-xxl-3 col-xl-4 theiaStickySidebar">
              <div className="stickybar">
                <div className="card">
                  <div className="card-body p-3">
                    {/* Filtros */}
                    <div className="border-bottom pb-4 mb-4">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <h5 className="mb-0">Filtros</h5>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PrintIcon />}
                          onClick={handlePrintCalendar}
                          title="Imprimir o Guardar Calendario como PDF"
                        >
                          Imprimir / PDF
                        </Button>
                      </Box>

                      {/* Filtro de fechas */}
                      <TextField
                        label="Fecha inicio"
                        type="date"
                        value={filtrosTemp.fechaInicio}
                        onChange={(e) => setFiltrosTemp((prev) => ({ ...prev, fechaInicio: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Fecha fin"
                        type="date"
                        value={filtrosTemp.fechaFin}
                        onChange={(e) => setFiltrosTemp((prev) => ({ ...prev, fechaFin: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                      />

                      {/* Filtro de estados */}
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Estados
                      </Typography>
                      {["PENDIENTE", "EN_PROCESO", "COMPLETADA", "REPROGRAMADA", "CANCELADA"].map((estado) => (
                        <FormControlLabel
                          key={estado}
                          control={
                            <Checkbox
                              checked={filtrosTemp.estados.includes(estado)}
                              onChange={() => handleEstadoChange(estado)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">{estado.charAt(0) + estado.slice(1).toLowerCase()}</Typography>
                          }
                          sx={{ display: "flex", mb: 0.5 }}
                        />
                      ))}

                      {/* Filtro de usuarios (multitag) */}
                      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                        Usuarios
                      </Typography>
                      <Autocomplete
                        multiple
                        options={usuariosFake}
                        value={usuariosFake.filter((u) => filtrosTemp.usuarios.includes(u.usrcodigo))}
                        onChange={handleUsuariosChange}
                        getOptionLabel={(option) => `${option.usrnombre} (${option.usrcodigo})`}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Seleccionar usuarios..." size="small" />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.usrcodigo}
                              label={option.usrnombre}
                              size="small"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        limitTags={2}
                        sx={{ mb: 2 }}
                      />

                      {/* Botones de acción */}
                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          onClick={handleAplicarFiltros}
                          sx={{ flex: 2 }}
                        >
                          Aplicar Filtros
                        </Button>
                        <Button variant="outlined" size="small" onClick={handleLimpiarFiltros} sx={{ flex: 1 }}>
                          Limpiar
                        </Button>
                      </Box>
                    </div>

                    <div className="border-bottom pb-4 mb-4">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h5 className="mb-0">Usuarios</h5>
                        <IconButton
                          size="small"
                          onClick={() => setModalUsuariosOpen(true)}
                          sx={{
                            backgroundColor: "primary.main",
                            color: "white",
                            "&:hover": { backgroundColor: "primary.dark" },
                          }}
                        >
                          <Groups />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        Total: {usuariosFake.length} usuarios
                      </Typography>
                      <Button fullWidth variant="outlined" onClick={() => setModalUsuariosOpen(true)} sx={{ mt: 1 }}>
                        Ver todos los usuarios
                      </Button>
                    </div>

                    <div className="border-bottom pb-4 mb-4">
                      <h5 className="mb-2">
                        Estadísticas de Eventos
                        <span className="badge badge-success rounded-pill ms-2">{estadisticas.totalEventos}</span>
                      </h5>
                      <Box display="flex" flexDirection="column" gap={1} mt={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Pendientes:</Typography>
                          <Chip
                            label={estadisticas.eventosPendientes}
                            size="small"
                            sx={{
                              bgcolor: "#ffeb3b",
                              color: "#000",
                              fontWeight: "bold",
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">En proceso:</Typography>
                          <Chip
                            label={estadisticas.eventosEnProceso}
                            size="small"
                            sx={{
                              bgcolor: "#2196f3",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Completados:</Typography>
                          <Chip
                            label={estadisticas.eventosCompletados}
                            size="small"
                            sx={{
                              bgcolor: "#4caf50",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Reprogramados:</Typography>
                          <Chip
                            label={estadisticas.eventosReprogramados}
                            size="small"
                            sx={{
                              bgcolor: "#ff9800",
                              color: "#000",
                              fontWeight: "bold",
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Cancelados:</Typography>
                          <Chip
                            label={estadisticas.eventosCancelados}
                            size="small"
                            sx={{
                              bgcolor: "#f44336",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        </Box>
                      </Box>
                    </div>

                    <div className="mb-4">
                      <h6 className="mb-2">Paquetes Disponibles</h6>
                      {paquetesFake?.paquetes?.map((paquete) => (
                        <div key={paquete.formcodigo} className="d-flex align-items-center mb-1">
                          <i className="ti ti-package text-info me-2"></i>
                          <small>
                            {paquete.formdescri} [{paquete.formcodigo}]
                          </small>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Calendar Main */}
            <div className="col-xxl-9 col-xl-8 theiaStickySidebar">
              <div className="stickybar">
                <div className="card border-0">
                  <div className="card-body" style={{ padding: "10px" }}>
                    {isLoading && (
                      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography>Cargando eventos...</Typography>
                      </Box>
                    )}
                    {!isLoading && (
                      <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        initialDate={filtros.fechaInicio}
                        locale={esLocale}
                        firstDay={0}
                        events={eventos}
                        headerToolbar={{
                          start: "today,prev,next",
                          center: "title",
                          end: "dayGridMonth,timeGridWeek,timeGridDay",
                        }}
                        eventClick={handleEventClick}
                        select={handleDateSelect}
                        selectable={true}
                        height="auto"
                        eventDisplay="block"
                        dayMaxEvents={false}
                        dayMaxEventRows={false}
                        moreLinkClick={false}
                        eventMaxStack={999}
                        eventOrder="start,title"
                        eventTimeFormat={{
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }}
                        eventContent={renderEventContent}
                        views={{
                          dayGridMonth: {
                            dayMaxEventRows: false,
                            dayMaxEvents: false,
                            dayCellMaxEvents: 999,
                            dayCellContent: (cellInfo) => {
                              const dateStr = dayjs(cellInfo.date).format("YYYY-MM-DD")
                              const eventosDelDia = eventos.filter(
                                (evento) => dayjs(evento.start).format("YYYY-MM-DD") === dateStr,
                              )

                              return {
                                html: `
                                  <div style="display: flex; flex-direction: column;">
                                    <div>${cellInfo.dayNumberText}</div>
                                    ${
                                      eventosDelDia.length > 0
                                        ? `
                                      <div style="font-size: 0.7rem; color: #666; margin-top: 2px;">
                                        ${eventosDelDia.length} evento${eventosDelDia.length !== 1 ? "s" : ""}
                                      </div>
                                    `
                                        : ""
                                    }
                                  </div>
                                `,
                              }
                            },
                          },
                          timeGridWeek: {
                            dayMaxEvents: false,
                            allDaySlot: false,
                            slotMinTime: "00:00:00",
                            slotMaxTime: "24:00:00",
                            expandRows: true,
                          },
                          timeGridDay: {
                            dayMaxEvents: false,
                            allDaySlot: false,
                            slotMinTime: "00:00:00",
                            slotMaxTime: "24:00:00",
                            expandRows: true,
                          },
                        }}
                        eventDidMount={handleEventDidMount}
                        selectMirror={true}
                        selectLongPressDelay={100}
                        eventLongPressDelay={100}
                        longPressDelay={100}
                        loading={isLoading}
                        eventBackgroundColor="transparent"
                        eventBorderColor="transparent"
                        contentHeight="auto"
                        aspectRatio={1.35}
                        scrollTimeReset={false}
                        scrollTime={false}
                        fixedWeekCount={false}
                        showNonCurrentDates={true}
                        navLinks={true}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Box>
        )}

        {/* Modal de Planificación de Paquetes */}
        <ModalPlanificacionPaquetes
          open={showPlanificacionModal}
          onClose={() => setShowPlanificacionModal(false)}
          selectedDate={selectedDate}
          eventosExistentes={eventos}
          onGuardarPlanificacion={handleGuardarPlanificacion}
        />

        {/* Modal Detalles del Evento */}
        <Dialog open={showDetailsModal} onClose={() => setShowDetailsModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Detalles de la Tarea</DialogTitle>
          <DialogContent>
            {selectedEvent && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedEvent.title}</Typography>
                  <Box display="flex" gap={1} alignItems="center" mb={2}>
                    <Chip
                      label={selectedEvent.extendedProps?.status || "Sin estado"}
                      size="small"
                      sx={{
                        backgroundColor: selectedEvent.backgroundColor,
                        color: selectedEvent.textColor,
                        fontWeight: "bold",
                      }}
                    />
                    <Chip label={selectedEvent.extendedProps?.cliente || "Sin cliente"} color="primary" size="small" />
                    {selectedEvent.extendedProps?.referenciaeventocodigoreprogramado && (
                      <Chip
                        label={`Evento código reprogramado ${selectedEvent.extendedProps.referenciaeventocodigoreprogramado}`}
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Fecha:
                  </Typography>
                  <Typography>{dayjs(selectedEvent.start).format("DD/MM/YYYY")}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Horario:
                  </Typography>
                  <Typography>
                    {dayjs(selectedEvent.start).format("HH:mm")} - {dayjs(selectedEvent.end).format("HH:mm")}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Duración:
                  </Typography>
                  <Typography>{selectedEvent.extendedProps?.duracion || 0} minutos</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Avance:
                  </Typography>
                  <Typography>{selectedEvent.extendedProps?.avance || 0}%</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Usuario:
                  </Typography>
                  <Typography>{selectedEvent.extendedProps?.usuario || "Sin usuario"}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Cliente:
                  </Typography>
                  <Typography>{selectedEvent.extendedProps?.cliente || "Sin cliente"}</Typography>
                </Grid>

                {selectedEvent.extendedProps?.recurrencia && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Recurrencia:
                    </Typography>
                    <Typography>
                      {selectedEvent.extendedProps.recurrencia}
                      {selectedEvent.extendedProps.recurrencia_num &&
                        ` (${selectedEvent.extendedProps.recurrencia_secuen || 0}/${selectedEvent.extendedProps.recurrencia_num})`}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    ID Evento:
                  </Typography>
                  <Typography variant="caption">{selectedEvent.id}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Código Cliente:
                  </Typography>
                  <Typography variant="caption">{selectedEvent.extendedProps?.clicodigo || "N/A"}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Código Usuario:
                  </Typography>
                  <Typography variant="caption">{selectedEvent.extendedProps?.usrcodigo || "N/A"}</Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            {selectedEvent?.extendedProps?.status && (
              <Button onClick={() => handleDeleteTarea([{ id: selectedEvent?.id }])} color="error">
                Eliminar
              </Button>
            )}
            <Button onClick={() => setShowDetailsModal(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Usuarios */}
        <ModalUsuarios
          open={modalUsuariosOpen}
          onClose={() => setModalUsuariosOpen(false)}
          usuarios={usuariosFake}
          eventos={eventos}
          onDeleteTarea={handleDeleteTarea}
          loading={isLoading}
        />
      </div>
    </ThemeProvider>
  )
}

export default CrearPlanificacionTareas
